// Daily closes with pins where insiders traded. Built on TradingView's
// Lightweight Charts; its attribution logo stays on, as the licence asks.
//
// Pins are grouped by day, week, month or quarter depending on how much of the
// chart is in view and how wide it is, so they never pile on top of each other: one pin above the line for
// the period's sales, one below for its purchases and one on the line for
// everything else, each sized by dollar value and labelled with its count.

import {
  AreaSeries, createChart, createSeriesMarkers, type IChartApi, type ISeriesApi, type ISeriesMarkersPluginApi, type SeriesMarker, type Time,
} from "lightweight-charts";
import { useEffect, useMemo, useRef, useState } from "react";

import { bucketStart, groupTrades, monthsBefore, pinUnit, type Bar, type PinGroup, type PinUnit, type Trade } from "../insider";

const css = (name: string) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();

/** A business day as YYYY-MM-DD, whichever form the chart hands back. */
function dayOf(time: Time): string {
  if (typeof time === "string") return time;
  if (typeof time === "number") return new Date(time * 1000).toISOString().slice(0, 10);
  return `${time.year}-${String(time.month).padStart(2, "0")}-${String(time.day).padStart(2, "0")}`;
}

/** 1 up to $100K, then a third of a step per tenfold, to at most 2 at $100M. */
export function pinSize(value: number): number {
  return value > 1e5 ? Math.min(2, 1 + Math.log10(value / 1e5) / 3) : 1;
}

export function markersFor(groups: Iterable<PinGroup>, colors: { up: string; down: string; other: string }): SeriesMarker<Time>[] {
  const markers: SeriesMarker<Time>[] = [];
  for (const group of groups) {
    const buys = group.trades.filter((t) => t.type === "purchase");
    const sells = group.trades.filter((t) => t.type === "sale");
    const others = group.trades.length - buys.length - sells.length;
    const total = (list: Trade[]) => list.reduce((sum, t) => sum + (t.value ?? 0), 0);
    const label = (letter: string, n: number) => (n > 1 ? `${letter}${n}` : letter);
    if (buys.length) markers.push({ time: group.time, position: "belowBar", shape: "arrowUp", color: colors.up, size: pinSize(total(buys)), text: label("B", buys.length) });
    if (sells.length) markers.push({ time: group.time, position: "aboveBar", shape: "arrowDown", color: colors.down, size: pinSize(total(sells)), text: label("S", sells.length) });
    if (others) markers.push({ time: group.time, position: "inBar", shape: "circle", color: colors.other, size: 0.8, text: others > 1 ? String(others) : "" });
  }
  return markers.sort((a, b) => String(a.time).localeCompare(String(b.time)));
}

/** `months` is the window shown, ending at the last bar; 0 shows every bar. */
export function InsiderChart({ bars, trades, months, onHover }: { bars: readonly Bar[]; trades: readonly Trade[]; months: number; onHover: (group: PinGroup | null) => void }) {
  const box = useRef<HTMLDivElement>(null);
  const chart = useRef<IChartApi | null>(null);
  const series = useRef<ISeriesApi<"Area"> | null>(null);
  const pins = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
  const [unit, setUnit] = useState<PinUnit>("week");
  const groups = useMemo(() => groupTrades(trades, bars, unit), [trades, bars, unit]);
  const hover = useRef({ onHover, groups, unit });
  hover.current = { onHover, groups, unit };

  useEffect(() => {
    const signal = css("--signal");
    const c = createChart(box.current!, {
      autoSize: true,
      layout: { background: { color: "transparent" }, textColor: css("--ink-muted"), fontFamily: css("--mono"), fontSize: 11, attributionLogo: true },
      grid: { vertLines: { visible: false }, horzLines: { color: css("--line") } },
      rightPriceScale: { borderColor: css("--line"), scaleMargins: { top: 0.15, bottom: 0.12 } },
      timeScale: { borderColor: css("--line") },
      crosshair: { horzLine: { labelBackgroundColor: css("--signal-fill") }, vertLine: { labelBackgroundColor: css("--signal-fill") } },
    });
    series.current = c.addSeries(AreaSeries, { lineColor: signal, topColor: `${signal}55`, bottomColor: `${signal}00`, lineWidth: 2, priceLineVisible: false });
    pins.current = createSeriesMarkers(series.current, []);
    c.subscribeCrosshairMove((param) => {
      const { onHover, groups, unit } = hover.current;
      onHover(param.time === undefined ? null : groups.get(bucketStart(dayOf(param.time), unit)) ?? null);
    });
    c.timeScale().subscribeVisibleTimeRangeChange((range) => {
      if (range) setUnit(pinUnit((Date.parse(dayOf(range.to)) - Date.parse(dayOf(range.from))) / 86_400_000, box.current?.clientWidth ?? 600));
    });
    chart.current = c;
    return () => {
      c.remove();
      chart.current = null;
    };
  }, []);

  useEffect(() => {
    series.current?.setData(bars.map(([time, value]) => ({ time, value })));
  }, [bars]);

  useEffect(() => {
    const last = bars.at(-1)?.[0];
    const scale = chart.current?.timeScale();
    if (!last || !scale) return;
    if (months && monthsBefore(last, months) > bars[0]![0]) scale.setVisibleRange({ from: monthsBefore(last, months), to: last });
    else scale.fitContent();
  }, [bars, months]);

  useEffect(() => {
    pins.current?.setMarkers(markersFor(groups.values(), { up: css("--up"), down: css("--down"), other: css("--signal") }));
  }, [groups]);

  return <div ref={box} style={{ height: 380 }} role="img" aria-label="Price chart with insider trade markers" />;
}
